import {
  Grid,
  Box,
  TextField,
  Button,
  Switch,
  MenuItem,
  Typography,
} from "@material-ui/core";
import { useState } from "react";
import Axios from "axios";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

import { useAdminStyles } from "../../components/admin/adminStyles";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { useToast } from "../../components/shared/Toast";

import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import InsertPhotoOutlinedIcon from "@material-ui/icons/InsertPhotoOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";

const headKey = "qweqeasdj123n1jasndjn2j3njsdn12i891bdjsdu1283djsd1231231";
const PRIORITY_OPTIONS = [1, 2, 3, 4, 5, 6];

export const AddClient = () => {
  const classes = useAdminStyles();
  const navigate = useNavigate();
  const toast = useToast();

  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [clientPic, setClientPic] = useState({ raw: "", preview: "" });
  const [priority, setPriority] = useState("");
  const [status, setStatus] = useState(true);
  const [err, setErr] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const handleImageChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Please select only JPEG, JPG, PNG or WebP images.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5 MB.");
      return;
    }
    setClientPic({
      preview: URL.createObjectURL(file),
      raw: file,
    });
  };

  const onSubmit = () => {
    setErr(0);
    if (!name.trim()) {
      setErr(4);
      toast.error("Please enter the client name.");
      return;
    }
    if (!url.trim()) {
      setErr(1);
      toast.error("Please enter the website URL.");
      return;
    }
    if (!priority) {
      setErr(2);
      toast.error("Please select a priority.");
      return;
    }
    if (!clientPic.raw) {
      setErr(3);
      toast.error("Please select a client logo image.");
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append("imgurl", clientPic.raw);
    formData.append("siteurl", url.trim());
    formData.append("priority", priority);
    formData.append("name", name.trim());
    formData.append("status", status ? "1" : "0");

    Axios.post("/api/add_client", formData, {
      headers: { headKey: headKey },
    })
      .then((res) => {
        setSubmitting(false);
        if (res.data.status) {
          toast.success(res.data.msg || "Client added");
          navigate("/dash/allclients");
        } else {
          toast.error(res.data.msg || "Something went wrong. Try again.");
        }
      })
      .catch(() => {
        setSubmitting(false);
        toast.error("Network error. Please try again.");
      });
  };

  return (
    <div>
      <Helmet>
        <title>Add Client - Admin | Arawinz Soft Solutions</title>
      </Helmet>

      <AdminPageHeader
        eyebrow="Clients"
        title="Add new client"
        subtitle="Add a client logo to display on the public site."
        backTo="/dash/allclients"
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Client name"
            variant="outlined"
            error={err === 4}
            value={name}
            size="small"
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Website URL"
            variant="outlined"
            error={err === 1}
            value={url}
            size="small"
            onChange={(e) => setUrl(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
            placeholder="https://example.com"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Priority"
            variant="outlined"
            error={err === 2}
            value={priority}
            size="small"
            onChange={(e) => setPriority(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">Select...</MenuItem>
            {PRIORITY_OPTIONS.map((n) => (
              <MenuItem key={n} value={String(n)}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className={classes.statusRow}>
            <Switch
              checked={status}
              onChange={() => setStatus(!status)}
              color="primary"
            />
            <span>
              Status:{" "}
              <span className={status ? "on" : "off"}>
                {status ? "Visible" : "Hidden"}
              </span>
            </span>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>Logo image</Typography>
          <Box className={classes.uploadWrap}>
            <input
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageChange}
              id="client-image-upload"
              type="file"
            />
            <label htmlFor="client-image-upload" style={{ display: "block" }}>
              <Box
                className={`${classes.uploadBox} ${
                  clientPic.raw ? classes.uploadBoxFilled : ""
                }`}
              >
                <Box className={classes.uploadIconWrap}>
                  {clientPic.raw ? (
                    <InsertPhotoOutlinedIcon />
                  ) : (
                    <CloudUploadOutlinedIcon />
                  )}
                </Box>
                <Box className={classes.uploadInfo}>
                  {clientPic.raw ? (
                    <>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <CheckCircleOutlineIcon
                          style={{ fontSize: 16, color: "#10b981" }}
                        />
                        <strong>{clientPic.raw.name}</strong>
                      </span>
                      <Typography className={classes.uploadHint}>
                        Click to choose a different file
                      </Typography>
                    </>
                  ) : (
                    <>
                      <span>
                        <strong>Upload client logo</strong>
                      </span>
                      <Typography className={classes.uploadHint}>
                        JPEG, PNG or WebP, up to 5 MB
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </label>
            {clientPic.preview && (
              <img
                src={clientPic.preview}
                alt="preview"
                className={classes.uploadPreview}
                style={{ maxWidth: 200, maxHeight: 160, background: "#ffffff" }}
              />
            )}
            {err === 3 && (
              <Typography style={{ color: "#dc2626", fontSize: "0.78rem" }}>
                Please select an image.
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      <Box className={classes.formFooter}>
        <Button
          className={classes.secondaryBtn}
          onClick={() => navigate("/dash/allclients")}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={submitting}
          className={classes.primaryBtn}
          endIcon={!submitting && <ArrowForwardIcon />}
        >
          {submitting ? "Adding..." : "Add Client"}
        </Button>
      </Box>
    </div>
  );
};
